import { Injectable } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageBusService } from 'app/services/messagebus/messagebus.service';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  fromEvent,
  Observable,
  Subject,
} from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { onElementResize } from 'common/utils/resize';
import { filterDefined } from 'utils/is-defined';

import { FormService } from './form.service';

import { ISPFieldConfig, ISPFieldWrapper } from '../model';
import { findConfig } from '../utils/formly-configs';

export const DYNAMIC_FORM_HEADER_CLASS = 'isp-dynamic-form-header';

export const DYNAMIC_FORM_HEADER_SELECTOR = `.${DYNAMIC_FORM_HEADER_CLASS}`;

export const DYNAMIC_FORM_FOOTER_CLASS = 'isp-dynamic-form-footer';

export const DYNAMIC_FORM_FOOTER_SELECTOR = `.${DYNAMIC_FORM_FOOTER_CLASS}`;

export const DYNAMIC_FORM_ASIDE_CLASS = 'isp-dynamic-form-aside';

export const DYNAMIC_FORM_ASIDE_SELECTOR = `.${DYNAMIC_FORM_ASIDE_CLASS}`;

export const DYNAMIC_FORM_SCROLLABLE_CONTAINER_CLASS =
  'isp-dynamic-form-scrollable-container';

export const DYNAMIC_FORM_SCROLLABLE_CONTAINER_SELECTOR = `.${DYNAMIC_FORM_SCROLLABLE_CONTAINER_CLASS}`;

/**
 * Dynamic form layout service
 */
@UntilDestroy()
@Injectable()
export class LayoutService {
  private static idCounter = 0;

  /** Form layout element subject */
  private readonly formLayoutSubject: BehaviorSubject<HTMLElement | undefined> =
    new BehaviorSubject(undefined);

  /** Form scrollable element subject */
  private readonly formScrollableContainerSubject: BehaviorSubject<
    HTMLElement | undefined
  > = new BehaviorSubject(undefined);

  /** Form header element subject */
  private readonly formHeaderSubject: BehaviorSubject<HTMLElement | undefined> =
    new BehaviorSubject(undefined);

  /** Form content element subject */
  private readonly formContentSubject: BehaviorSubject<
    HTMLElement | undefined
  > = new BehaviorSubject(undefined);

  /** Form footer element subject */
  private readonly formFooterSubject: BehaviorSubject<HTMLElement | undefined> =
    new BehaviorSubject(undefined);

  /** Form aside element subject */
  private readonly formAsideSubject: BehaviorSubject<HTMLElement | undefined> =
    new BehaviorSubject(undefined);

  /** Subject for stream scroll form content event */
  private readonly scrollFormSubject: Subject<[number, number]> = new Subject();

  readonly formId = `isp-form_${LayoutService.idCounter++}`;

  /** Form scrollable element stream. Used to store form element, that can be manually scrolled */
  readonly formContainer$ = this.formScrollableContainerSubject.pipe(
    filterDefined(),
  );

  get formLayout(): HTMLElement | undefined {
    return this.formLayoutSubject.value;
  }

  set formLayout(element: HTMLElement | undefined) {
    this.formLayoutSubject.next(element);
  }

  get formScrollableContainer(): HTMLElement | undefined {
    return this.formScrollableContainerSubject.value;
  }

  set formScrollableContainer(element: HTMLElement | undefined) {
    if (element) {
      element.classList.add(DYNAMIC_FORM_SCROLLABLE_CONTAINER_CLASS);
    }

    this.formScrollableContainerSubject.next(element);
  }

  get formHeader(): HTMLElement | undefined {
    return this.formHeaderSubject.value;
  }

  set formHeader(element: HTMLElement | undefined) {
    if (element) {
      element.classList.add(DYNAMIC_FORM_HEADER_CLASS);
    }

    this.formHeaderSubject.next(element);
  }

  get formContent(): HTMLElement | undefined {
    return this.formContentSubject.value;
  }

  set formContent(element: HTMLElement | undefined) {
    this.formContentSubject.next(element);
  }

  get formFooter(): HTMLElement | undefined {
    return this.formFooterSubject.value;
  }

  set formFooter(element: HTMLElement | undefined) {
    if (element) {
      element.classList.add(DYNAMIC_FORM_FOOTER_CLASS);
    }

    this.formFooterSubject.next(element);
  }

  get formAside(): HTMLElement | undefined {
    return this.formAsideSubject.value;
  }

  set formAside(element: HTMLElement | undefined) {
    if (element) {
      element.classList.add(DYNAMIC_FORM_ASIDE_CLASS);
    }

    this.formAsideSubject.next(element);
  }

  constructor(
    private readonly bus: MessageBusService,
    private readonly formService: FormService,
  ) {
    this.watchForHeaderHeight();
    this.watchForFormScroll();
  }

  private watchForHeaderHeight(): void {
    combineLatest([
      this.formLayoutSubject.pipe(filterDefined()),
      this.formHeaderSubject.pipe(
        filterDefined(),
        switchMap(header => onElementResize(header, true)),
      ),
    ])
      .pipe(
        tap(([layout, header]) => {
          const height = `${header.clientHeight}px`;
          layout.style.setProperty('--isp-layout-header-height', height);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private watchForFormScroll(): void {
    const formScrollEvent$ = this.formScrollableContainerSubject.pipe(
      filterDefined(),
      switchMap(container => fromEvent(container, 'scroll')),
    );

    formScrollEvent$
      .pipe(
        switchMap(() =>
          combineLatest([
            this.formContentSubject.pipe(filterDefined()),
            this.formScrollableContainerSubject.pipe(filterDefined()),
            this.formFooterSubject,
          ]),
        ),
        tap(([content, form, footer]) => {
          const rectContent = content.getBoundingClientRect();
          const upperBound = form.scrollTop + rectContent.top;
          const bottomBound = footer
            ? footer.getBoundingClientRect().top
            : rectContent.bottom;

          this.scrollFormSubject.next([upperBound, bottomBound]);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Check on need close dropdown if anchor not visible
   *
   * @param anchorElement - Html element anchor dropdown container
   * @param upperBound - upper bound form content
   * @param bottomBound - bottom bound form content
   */
  private needCloseDropdown(
    anchorElement: HTMLElement,
    upperBound: number,
    bottomBound: number,
  ): boolean {
    const rect = anchorElement.getBoundingClientRect();
    return (
      rect.top + anchorElement.offsetHeight < upperBound ||
      rect.bottom - anchorElement.offsetHeight > bottomBound
    );
  }

  getScrollEventStream$(): Observable<number[]> {
    const selector = this.formService.options?.formState.dropdownScrollSelector;
    if (!selector) {
      return this.scrollFormSubject.asObservable();
    }

    const element = document.querySelector(selector);
    if (!element) {
      return this.scrollFormSubject.asObservable();
    }

    return fromEvent(element, 'scroll').pipe(
      map(() => {
        const wrapperRect = element.getBoundingClientRect();
        return [wrapperRect.top, wrapperRect.bottom];
      }),
    );
  }

  /**
   * Get config of page for provided field. Return undefined, if field is not in page
   *
   * @param config - field config
   */
  getFieldPage(config: ISPFieldConfig): ISPFieldConfig | undefined {
    // if field is inside a closed page, we should open it
    const page = findConfig(this.formService.configs, fieldConfig => {
      return (
        Boolean(fieldConfig.wrappers?.includes(ISPFieldWrapper.Page)) &&
        fieldConfig.fieldGroup !== undefined &&
        findConfig(fieldConfig.fieldGroup, ff => ff.key === config.key) !==
          undefined
      );
    });

    return page;
  }

  /**
   * Stream when need close dropdown on scroll
   *
   * @param anchorElement - dropdown anchor element
   */
  needCloseDropdown$(anchorElement: HTMLElement): Observable<number[]> {
    // need close dropdown only with scroll element with custom selector
    if (!this.formService.options?.formState.dropdownScrollSelector) {
      return EMPTY;
    }

    const observable = this.getScrollEventStream$();
    return observable.pipe(
      debounceTime(50),
      filter(([scrollTop, scrollBottom]) =>
        this.needCloseDropdown(anchorElement, scrollTop, scrollBottom),
      ),
    );
  }

  /**
   * Toggle page state
   *
   * @param page - page config
   * @param openState - page opening state
   */
  togglePage(page: ISPFieldConfig, openState?: boolean): Promise<void> {
    if (!page.props.name) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      this.bus
        .emit('form-collapse-toggle', {
          name: page.props.name!,
          openState,
        })
        .then(resolve);
    });
  }

  /**
   * Toggle page state of provided field. If field is not in page then resolved instantly
   *
   * @param config - field config
   * @param openState - page opening state
   */
  toggleFieldPage(config: ISPFieldConfig, openState?: boolean): Promise<void> {
    const page = this.getFieldPage(config);

    if (page) {
      return this.togglePage(page, openState);
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Check that field or some DOM element in form view (between header and footer)
   *
   * @param param - DOM element of field config
   */
  isInFormView(param: Element | ISPFieldConfig): boolean {
    let element: Element | null = null;
    if (!(param instanceof Element)) {
      if (param.id) {
        element = document.getElementById(param.id);
      }
    } else {
      element = param;
    }
    if (!element) {
      return false;
    }

    const header = this.formHeader?.getBoundingClientRect();
    const footer = this.formFooter?.getBoundingClientRect();
    const form = this.formScrollableContainer?.getBoundingClientRect();
    const field = element.getBoundingClientRect();

    const isInForm =
      form !== undefined &&
      field.top >= form.top &&
      field.bottom <= form.bottom;
    const isBelowHeader = header ? field.top >= header.bottom : true;
    const isAboveFooter = footer ? field.bottom <= footer.top : true;

    return isInForm && isBelowHeader && isAboveFooter;
  }

  /**
   * Change form mode and open form pages of field, if needed
   *
   * @param config - field config
   */
  async navigateToField(config: ISPFieldConfig): Promise<void> {
    await this.toggleFieldPage(config, true);
  }

  /**
   * Scroll to field. Automatically open field page and toggle form mode if needed
   *
   * @param config - field config
   * @param ifNeeded - check that element already in form view. If so, no scroll will be applied
   */
  async scrollToField(
    config: ISPFieldConfig,
    ifNeeded?: boolean,
  ): Promise<void> {
    if (!config.id) {
      return;
    }
    await this.navigateToField(config);
    const element = document.getElementById(config.id);
    if (!element) {
      return;
    }
    return this.scrollTo(element, ifNeeded);
  }

  /**
   * Scroll to element on the form
   *
   * @param elementOrSelector - html element or selector string
   * @param ifNeeded - check that element already in form view. If so, no scroll will be applied
   */
  async scrollTo(
    elementOrSelector: HTMLElement | string,
    ifNeeded?: boolean,
  ): Promise<void> {
    let element: HTMLElement | null;
    if (typeof elementOrSelector === 'string') {
      element = document.querySelector(elementOrSelector);
    } else {
      element = elementOrSelector;
    }

    if (!element) {
      return Promise.reject(
        `LayoutService: No field with such query selector ${
          elementOrSelector as string
        }`,
      );
    }

    if (ifNeeded && this.isInFormView(element)) {
      return;
    }

    element.scrollIntoView();
  }
}
